import BottomSheet from '@gorhom/bottom-sheet';
import {
  EventType,
  PartnerOfferTopCategory,
  SortBy,
  VendorItem,
} from '@varo/graphql-schema-types';
import { Stack, useFocusEffect, useLocalSearchParams } from 'expo-router';
import {
  CATEGORY_ALL_ID,
  CATEGORY_ALL_LABEL,
} from 'features/rewards/constants/constants';
import useKardAttribution from 'features/rewards/hooks/useKardAttribution';
import useLocalVendors from 'features/rewards/hooks/useLocalVendors';
import useRewardsLocation from 'features/rewards/hooks/useRewardsLocation';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import getRewardsTopCategories from 'services/graphql/queries/rewards/TopCategories';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import PillGroup from 'shared/components/vxl/navigation/PillGroup/PillGroup';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

import ListViewScreen from './ListViewScreen';
import NearbyOffers from './NearbyOffers';

const NearbySelectionScreen = () => {
  const { t } = useTranslation('rewards', { keyPrefix: 'nearbyMap' });
  const animatedIndex = useSharedValue(0);

  const { analyticsEvents } = useAnalyticsConfiguration();
  const { category: categoryParam, name: nameParam } = useLocalSearchParams<{
    category?: string;
    name?: string;
  }>();

  const [categoryId, setCategoryId] = useState(
    categoryParam || CATEGORY_ALL_ID,
  );
  const [categoryName, setCategoryName] = useState(
    nameParam || CATEGORY_ALL_LABEL,
  );

  const { recordImpressions, formatVendorDataForKardImpressions } =
    useKardAttribution();

  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);

  const [mapSearchLocation, setMapSearchLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const mapCameraLocationRef = useRef<google.maps.LatLngLiteral | null>(null);
  const mapCameraZoomRef = useRef<number | null>(null);
  const mapCameraHeadingRef = useRef<number | null>(null);
  const bottomSheetRef = useRef<BottomSheet>(null);
  const isSheetOpenRef = useRef<boolean>(false);
  const isInitialLoadRef = useRef<boolean>(true);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);

  const insets = useSafeAreaInsets();
  const { coords, isLocationFetching, isLocationDenied } = useRewardsLocation();

  const effectiveCoords = mapSearchLocation
    ? { latitude: mapSearchLocation.lat, longitude: mapSearchLocation.lng }
    : coords;

  const { localVendors, localSections, loading, error, reload } =
    useLocalVendors({
      categoryId,
      latitude: effectiveCoords?.latitude,
      longitude: effectiveCoords?.longitude,
      pause: isLocationFetching || !effectiveCoords,
    });

  // Track fluid updates to 'reload' reference via a persistent ref container
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  const [topCategoriesResult, refetchTopCategories] = useQuery({
    query: getRewardsTopCategories,
    variables: { sortTopCategoryBy: SortBy.OfferOrder },
    requestPolicy: 'cache-first',
  });

  const topCategoriesData = topCategoriesResult.data;
  const topCategoriesFetching = topCategoriesResult.fetching;

  const topCategoriesError = topCategoriesResult.error;

  const items = (topCategoriesData?.myPartnerOffers?.topCategories ?? [])
    .filter((c: PartnerOfferTopCategory) => Boolean(c?.id && c?.name))
    .map((c: PartnerOfferTopCategory) => ({
      id: c.id,
      labelText: c.name,
      iconKey: c.iconKey ?? 'ELLIPSE',
      iconColor: !c.iconKey
        ? theme.color.interactive.secondaryPressed
        : theme.color.text.primary,
    }));

  const pillConfigs = [
    {
      id: CATEGORY_ALL_ID,
      labelText: CATEGORY_ALL_LABEL,
      iconKey: 'CASHBACK',
      iconColor: theme.color.text.primary,
    },
    ...items,
  ];

  const pageTitle = t('title');

  useFocusEffect(
    useCallback(() => {
      reloadRef.current();
    }, [reloadRef]),
  );

  const snapPoints = useMemo(
    () => [64 + insets.bottom, '100%'],
    [insets.bottom],
  );

  useFocusEffect(
    useCallback(() => {
      if (isSheetOpenRef.current) {
        setIsSheetOpen(true);
        const timer = setTimeout(() => {
          bottomSheetRef.current?.expand();
        }, 500);

        return () => clearTimeout(timer);
      }
    }, []), // Intentional empty array: only want to run this on focus
  );

  useFocusEffect(
    useCallback(() => {
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        return;
      }

      reload({ requestPolicy: 'cache-and-network' });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []), // Intentional empty array: prevents network refetch loops if the reload function instance changes
  );

  useEffect(() => {
    if (!loading && showLoadingSpinner) {
      setShowLoadingSpinner(false);
    }
  }, [loading]);

  useEffect(() => {
    if (localVendors.length > 0) {
      const medium = mapSearchLocation ? 'SEARCH' : 'MAP';
      const kardAttributionData = formatVendorDataForKardImpressions(
        localVendors.filter(
          (vendor: VendorItem) => vendor.offerSource === 'KARD',
        ),
        medium,
      );

      void recordImpressions(EventType.Impression, kardAttributionData);
    }
  }, [
    formatVendorDataForKardImpressions,
    localVendors,
    mapSearchLocation,
    recordImpressions,
  ]);

  useEffect(() => {
    setSelectedVendorId(null);
  }, [categoryId]);

  const buttonAnimatedStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      animatedIndex.value,
      [0.75, 0.85],
      [0, 1],
      'clamp',
    );

    return {
      opacity,
      display: isSheetOpen || animatedIndex.value > 0.75 ? 'flex' : 'none',
    };
  });

  const handleCategorySelection = (config: {
    id: string;
    labelText: string;
  }) => {
    if (config?.id) {
      analyticsEvents.logPressableEventFromConfig(
        '/rewards/nearby-offers/map/offer-type-selected',
        {
          offer_bracket: config.labelText,
        },
      );
      setShowLoadingSpinner(true);
      setCategoryId(config.id);
      setCategoryName(config.labelText);
    }
  };

  const handleMapLocationChange = (
    location: google.maps.LatLngLiteral,
  ): void => {
    if (
      location.lat !== mapSearchLocation?.lat ||
      location.lng !== mapSearchLocation?.lng
    ) {
      setMapSearchLocation(location);
      reload();
    }
  };

  const handleMapCameraLocationChange = (
    location: google.maps.LatLngLiteral,
    zoom?: number,
    heading?: number,
  ): void => {
    mapCameraLocationRef.current = location;
    mapCameraZoomRef.current = zoom ?? null;
    mapCameraHeadingRef.current = heading ?? null;
  };

  const handleOpenSheet = () => {
    isSheetOpenRef.current = true;
    setIsSheetOpen(true);
    bottomSheetRef.current?.expand();
  };

  const handleCloseSheet = () => {
    analyticsEvents.logPressableEventFromConfig(
      '/rewards/nearby-offers/map/offer-map-view-selected',
      {
        offer_bracket: categoryName,
      },
    );
    isSheetOpenRef.current = false;
    setIsSheetOpen(false);
    bottomSheetRef.current?.collapse();
  };

  const onSwipeSheet = (index: number) => {
    isSheetOpenRef.current = index > 0;
    setIsSheetOpen(index > 0);

    if (index > 0 && !isInitialLoadRef.current) {
      analyticsEvents.logPressableEventFromConfig(
        '/rewards/nearby-offers/map/offer-list-view-selected',
        {
          offer_bracket: categoryName,
        },
      );
    }
  };

  const shouldContentLoading = showLoadingSpinner && loading;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: pageTitle,
          headerBackVisible: true,
          headerShown: true,
          header: (props) => <ExpoNavigationBar {...props} />,
        }}
      />

      <GestureHandlerRootView style={styles.flexItem}>
        <NearbyOffers
          categoryId={categoryId}
          categoryName={categoryName}
          offers={localVendors}
          loading={shouldContentLoading}
          error={error}
          reload={reload}
          coords={effectiveCoords}
          isLocationFetching={isLocationFetching}
          isLocationDenied={isLocationDenied}
          initialMapLocation={mapCameraLocationRef.current ?? mapSearchLocation}
          initialMapZoom={mapCameraZoomRef.current ?? undefined}
          initialMapHeading={mapCameraHeadingRef.current ?? undefined}
          onMapLocationChange={handleMapLocationChange}
          selectedVendorId={selectedVendorId}
          setSelectedVendorId={setSelectedVendorId}
          onCameraLocationChange={handleMapCameraLocationChange}
        />

        <View style={styles.pillContainer}>
          <PillGroup
            key="categoryPills"
            initialSelectedPillId={categoryId}
            pillConfigs={pillConfigs}
            onSelection={handleCategorySelection}
          />
        </View>

        {!selectedVendorId ? (
          <BottomSheet
            ref={bottomSheetRef}
            snapPoints={snapPoints}
            index={isSheetOpenRef.current ? 1 : 0}
            animatedIndex={animatedIndex}
            enableOverDrag={false}
            handleIndicatorStyle={styles.sheetHandleStyle}
            backgroundStyle={styles.sheetBackgroundStyle}
            topInset={-30}
            enableHandlePanningGesture={!isSheetOpen}
            enableContentPanningGesture={!isSheetOpen}
            onChange={onSwipeSheet}
          >
            <ListViewScreen
              categoryId={categoryId}
              categoryName={categoryName}
              onOpenSheet={handleOpenSheet}
              sections={localSections}
              loading={shouldContentLoading}
              error={error}
              reload={reload}
              coords={effectiveCoords}
              totalOffersCount={localVendors.length}
              accessibilityState={{ expanded: isSheetOpenRef.current }}
            />
          </BottomSheet>
        ) : null}
      </GestureHandlerRootView>

      <ScreenTransitionOverlay
        isTabPage
        isLoading={topCategoriesFetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          topCategoriesError ? (
            <DefaultPageDownErrorMessage
              error={topCategoriesError}
              tryAgainAction={() =>
                refetchTopCategories({ requestPolicy: 'network-only' })
              }
            />
          ) : null
        }
      />

      <Animated.View
        pointerEvents="auto"
        style={[
          styles.buttonContainer,
          { bottom: insets.bottom + 20 },
          buttonAnimatedStyle,
        ]}
      >
        <Button
          label={t('buttonLabel')}
          onPress={handleCloseSheet}
          width="HUG"
        />
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  flexItem: {
    flex: 1,
  },
  pillContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 99,
    backgroundColor: theme.color.background.primary,
  },
  sheetHandleStyle: {
    backgroundColor: theme.color.text.muted,
    width: 36,
    height: 5,
    borderRadius: 100,
    alignSelf: 'center',
  },
  sheetBackgroundStyle: {
    backgroundColor: theme.color.background.primary,
    borderTopLeftRadius: theme.spacing.m,
    borderTopRightRadius: theme.spacing.m,
  },
  buttonContainer: {
    alignSelf: 'center',
    position: 'absolute',
    flexDirection: 'column',
    justifyContent: 'center',
    zIndex: 99,
  },
});

export default NearbySelectionScreen;
